import { lazy, Suspense, useEffect, useState } from "react";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { useAuth } from "@/hooks/useAuth";
import { FieldValues, useForm, useWatch } from "react-hook-form";

import { RequestTypes } from "../../../../constants/enums";
import { CounterDeliveryDetail } from "../schema/counter-delivery.schema";
import { CounterDeliveryAmountAndActions } from "@/components/common/section-components/requests/counter-delivery/CounterDeliveryAmountAndActions";
import {
  fetchCounterDeliveryHandler,
  handleExecuteCounterDeliveryHandler,
  handleRejectCounterDeliveryHandler,
  sendCounterDeliveryCommunicationHandler,
  sendCounterDeliveryNegotiationHandler,
  updateCounterDeliveryAmountHandler,
} from "../schema/handler-functions";
import { useRoles } from "@/hooks/useRoles";
import { CounterDeliveryDetailMainDataCard } from "@/components/common/section-components/card/CounterDeliveryMainDataCard";
import { AppColors } from "@/styles/colors";
import { CounterDeliveryDetailLegend } from "@/components/common/section-components/requests/counter-delivery/CounterDeliveryDetailLegend";
import { CounterDeliveryMessageList } from "@/components/common/section-components/requests/counter-delivery/CounterDeliveryMessageList";

export const CounterDeliveryDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.GRAIN_DELIVERY_PAYMENTS);
  };

  const [counterDelivery, setCounterDelivery] =
    useState<CounterDeliveryDetail | null>();
  const fetchCounterDelivery = async (showLoad: boolean = true) => {
    await fetchCounterDeliveryHandler({
      fetchPermissions,
      id,
      navigate,
      setCounterDelivery,
      setLoadingCounterDelivery,
      showLoad,
    });
  };

  const [loadingCounterDelivery, setLoadingCounterDelivery] =
    useState<boolean>(false);

  useEffect(() => {
    if (isActive && id && user) fetchCounterDelivery();
  }, [isActive, id, user]);

  const [isEditingAmount, setIsEditingAmount] = useState<boolean>(false);
  const [loadingEditAmount, setLoadingEditAmount] = useState<boolean>(false);
  const [loadingSendNegotiation, setLoadingSendNegotiation] =
    useState<boolean>(false);
  const [loadingSendCommunication, setLoadingSendCommunication] =
    useState<boolean>(false);

  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [loadingRejectCounterDelivery, setLoadingRejectCounterDelivery] =
    useState<boolean>(false);
  const [loadingExecuteCounterDelivery, setLoadingExecuteCounterDelivery] =
    useState<boolean>(false);

  const handleExecuteCounterDelivery = async (event?: FieldValues) => {
    await handleExecuteCounterDeliveryHandler({
      navigate,
      setLoadingExecuteCounterDelivery,
      counterDelivery,
      fetchPermissions,
      watchedValues: event,
    });
  };

  const { open: openExecuteModal, setOpen: setOpenExecuteModal } =
    useDisclosure();

  const handleRejectCounterDelivery = async (event?: FieldValues) => {
    await handleRejectCounterDeliveryHandler({
      navigate,
      setLoadingRejectCounterDelivery,
      setOpenRejectModal,
      event,
      counterDelivery,
      fetchPermissions,
    });
  };

  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm();

  const watchedValues = useWatch({
    control,
  });

  const handleEditAmount = async () => {
    await updateCounterDeliveryAmountHandler({
      errors,
      setLoadingEditAmount,
      counterDelivery,
      fetchCounterDelivery,
      setIsEditingAmount,
      watchedValues,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (counterDelivery && !counterDelivery?.sinBase) {
      setValue("sell_order_amount", counterDelivery?.montoSolicitado);
    }
  }, [counterDelivery]);

  const { setOpen: setOpenCommunication, open: openCommunication } =
    useDisclosure();

  const { fetchPermissions } = useRoles();

  const handleSendNegotiation = async () => {
    await sendCounterDeliveryNegotiationHandler({
      navigate,
      setLoadingSendNegotiation,
      counterDelivery,
      fetchCounterDelivery,
      fetchPermissions,
    });
  };

  const handleSendCommunication = async (event?: FieldValues) => {
    await sendCounterDeliveryCommunicationHandler({
      navigate,
      setLoadingSendCommunication,
      setOpenCommunicationModal: setOpenCommunication,
      event,
      counterDelivery,
      fetchCounterDelivery,
      fetchPermissions,
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={"Solicitud de venta de granos a entregar"}
        onBack={handleBack}
        backButton
        loading={loadingCounterDelivery}
      />

      {!loadingCounterDelivery && (
        <>
          <CounterDeliveryAmountAndActions
            clearErrors={clearErrors}
            counterDelivery={counterDelivery}
            setIsEditingAmount={setIsEditingAmount}
            control={control}
            errors={errors}
            handleEditAmount={handleEditAmount}
            loadingEditAmount={loadingEditAmount}
            isEditingAmount={isEditingAmount}
            setOpenRejectModal={setOpenRejectModal}
            loadingRejectCounterDelivery={loadingRejectCounterDelivery}
            loadingExecuteCounterDelivery={loadingExecuteCounterDelivery}
            handleExecuteCounterDelivery={() => setOpenExecuteModal(true)}
            setValue={setValue}
            loadingSendNegotiation={loadingSendNegotiation}
            loadingSendCommunication={loadingSendCommunication}
            setOpenInfoModal={setOpenCommunication}
            handleSendNegotiation={handleSendNegotiation}
          />

          <CounterDeliveryDetailMainDataCard
            counterDelivery={counterDelivery}
          />

          <Flex
            flexDir="column"
            padding="12px"
            height="100%"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            borderRadius="12px"
            gap="25px"
          >
            <CounterDeliveryDetailLegend />

            <CounterDeliveryMessageList counterDelivery={counterDelivery} />
          </Flex>
        </>
      )}

      {counterDelivery?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectCounterDelivery}
            loadingAccept={loadingRejectCounterDelivery}
            idTipoSolicitud={RequestTypes.ContraEntrega}
            entityName="venta de granos a entregar"
          />
          <NegotiationModalDynamic
            open={openCommunication}
            setOpen={setOpenCommunication}
            handleAccept={handleSendCommunication}
            idTipoSolicitud={RequestTypes.ContraEntrega}
            loadingAccept={loadingSendCommunication}
          />
          <ExecuteGrainRequestModalDynamic
            open={openExecuteModal}
            setOpen={setOpenExecuteModal}
            handleAccept={handleExecuteCounterDelivery}
            loadingAccept={loadingExecuteCounterDelivery}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);

const NegotiationModalDynamic = lazy(() =>
  import("@/components/modal/NegotiationModal").then((el) => ({
    default: el["NegotiationModal"],
  })),
);

const ExecuteGrainRequestModalDynamic = lazy(() =>
  import("@/components/modal/ExecuteGrainRequestModal").then((el) => ({
    default: el["ExecuteGrainRequestModal"],
  })),
);
